/**
 * Applicazione del tema.
 *
 * I colori provengono dal branding centralizzato e vengono scritti come CSS
 * custom properties: un cambio di marca si fa modificando un solo file, senza
 * toccare i componenti.
 */

import { branding } from '@/config/branding';

export type ThemePreference = 'light' | 'dark' | 'system';

function resolve(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function writeVariables(mode: 'light' | 'dark'): void {
  const palette = mode === 'dark' ? branding.darkPalette : branding.palette;
  const root = document.documentElement;

  root.style.setProperty('--primary', palette.primary);
  root.style.setProperty('--primary-foreground', palette.primaryForeground);
  root.style.setProperty('--secondary', palette.secondary);
  root.style.setProperty('--secondary-foreground', palette.secondaryForeground);
  root.style.setProperty('--accent', palette.accent);
  root.style.setProperty('--accent-foreground', palette.accentForeground);

  root.classList.toggle('dark', mode === 'dark');
  // Allinea la barra di sistema del browser al tema, così la PWA installata non
  // mostra una striscia chiara sopra un'interfaccia scura.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', mode === 'dark' ? '#0d0b12' : '#7b2d4e');
}

/**
 * Applica il tema e resta in ascolto delle preferenze di sistema.
 * Restituisce la funzione di pulizia da usare in `useEffect`.
 */
export function applyBrandTheme(preference: ThemePreference): () => void {
  writeVariables(resolve(preference));

  if (preference !== 'system') return () => undefined;

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (): void => writeVariables(resolve('system'));
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}
